import React, {useCallback, useState} from 'react';
import {injectIntl} from 'react-intl';
import {Field, reduxForm} from 'redux-form';
import {Button, FileUploader, FormGroup, Head, Textarea} from '@xsolla/uikit';
import classNames from 'classnames/bind';
import PropTypes from 'prop-types';
import {compose} from 'recompose';
import {uploadMultipleFiles} from '@api/file-manager';
import EvidenceProofConfirmModal from '../EvidenceProofConfirmModal/EvidenceProofConfirmModal';
import style from './style.css';

const cx = classNames.bind(style);
const MAX_FILE_SIZE = 2 * 1024 * 1024;
const MAX_FILES = 3;

const ACCEPT = 'application/pdf,image/jpeg,image/png';

const isPdf = async file => {
  if (file.type !== 'application/pdf' || !file.name.toLowerCase().endsWith('.pdf')) {
    return false;
  }

  const decoder = new TextDecoder();
  const header = decoder.decode(await file.slice(0, 5).arrayBuffer());

  if (header !== '%PDF-') {
    return false;
  }

  const trailer = decoder.decode(await file.slice(Math.max(0, file.size - 2048)).arrayBuffer());

  return trailer.includes('%%EOF');
};

const isJpeg = async file => {
  if (
    file.type !== 'image/jpeg' ||
    !(file.name.toLowerCase().endsWith('.jpg') || file.name.toLowerCase().endsWith('.jpeg'))
  ) {
    return false;
  }

  const header = new Uint8Array(await file.slice(0, 3).arrayBuffer());

  return header[0] === 0xff && header[1] === 0xd8 && header[2] === 0xff;
};

const isPng = async file => {
  if (file.type !== 'image/png' || !file.name.toLowerCase().endsWith('.png')) {
    return false;
  }

  const header = new Uint8Array(await file.slice(0, 8).arrayBuffer());
  const pngSignature = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a];

  return pngSignature.every((byte, index) => header[index] === byte);
};

const isAllowedFile = async file =>
  (await isPdf(file)) || (await isJpeg(file)) || (await isPng(file));

const ChargebackProofForm = ({
  intl,
  disputeId,
  transactionId,
  merchantId,
  onSubmit,
  onClose,
  isSubmitting,
  handleSubmit,
  setNotification
}) => {
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingFormValues, setPendingFormValues] = useState(null);

  const removeFile = useCallback(fileId => {
    setUploadedFiles(prev => prev.filter(file => file.fileId !== fileId));
  }, []);

  const handleFileUpload = useCallback(
    async files => {
      if (uploadedFiles.length + files.length > MAX_FILES) {
        alert(
          intl.formatMessage(
            {id: 'app.support.disputes.proof-form.error.max-files'},
            {maxFiles: MAX_FILES}
          )
        );
        return;
      }

      if (files.some(file => file.size === 0 || file.size > MAX_FILE_SIZE)) {
        alert(
          intl.formatMessage(
            {id: 'app.support.disputes.proof-form.error.file-size'},
            {maxSizeMb: MAX_FILE_SIZE / (1024 * 1024)}
          )
        );
        return;
      }

      if (files.length === 0) {
        return;
      }

      const validFiles = await Promise.all(files.map(isAllowedFile));

      if (validFiles.some(isValid => !isValid)) {
        alert(intl.messages['app.support.disputes.proof-form.error.invalid-file-type']);
        return;
      }

      setIsUploading(true);

      try {
        const uploadedFileData = await uploadMultipleFiles(merchantId, files);
        setUploadedFiles(prev => [...prev, ...uploadedFileData]);
      } catch (error) {
        alert(intl.messages['app.support.disputes.proof-form.error.upload-failed']);
      } finally {
        setIsUploading(false);
      }
    },
    [intl, merchantId, uploadedFiles]
  );

  const handleFormSubmit = useCallback(values => {
    setPendingFormValues(values);
    setShowConfirmModal(true);
  }, []);

  const handleConfirmSend = useCallback(() => {
    if (!pendingFormValues) {
      return;
    }
    const successText = intl.messages['app.support.disputes.proof-form.success.message'];
    const errorText = intl.messages['app.support.disputes.proof-form.error.message'];
    onSubmit(
      disputeId,
      {
        commentary: pendingFormValues.commentary || '',
        files: uploadedFiles
      },
      setNotification,
      {successText, errorText}
    );
    setShowConfirmModal(false);
    setPendingFormValues(null);
  }, [disputeId, onSubmit, uploadedFiles, intl, pendingFormValues, setNotification]);

  const handleCancelConfirm = useCallback(() => {
    setShowConfirmModal(false);
    setPendingFormValues(null);
  }, []);

  return (
    <>
      <form
        className={cx('chargeback-proof-form')}
        onSubmit={handleSubmit(handleFormSubmit)}
        style={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%'
        }}>
        <div
          className={cx('chargeback-proof-form__header')}
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '20px'
          }}>
          <Head>{intl.messages['app.support.disputes.proof-form.modal.title']}</Head>
          <Button appearance="flatten" size="sm" icon="close" onClick={onClose} />
        </div>

        <FormGroup
          label={intl.messages['app.support.disputes.proof-form.transaction-ids']}
          style={{marginBottom: '20px'}}>
          <input
            type="text"
            value={transactionId}
            readOnly={true}
            style={{
              width: '100%',
              padding: '10px 12px',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              backgroundColor: '#f9fafb',
              fontSize: '14px',
              color: '#374151'
            }}
          />
        </FormGroup>

        <div key="commentary-field" className={cx('chargeback-proof-form__field')}>
          <FormGroup label={intl.messages['app.support.disputes.proof-form.commentary.label']}>
            <Field
              key="commentary"
              name="commentary"
              component={Textarea}
              rows={4}
              placeholder={intl.messages['app.support.disputes.proof-form.commentary.placeholder']}
            />
          </FormGroup>
        </div>

        <div key="files-field" className={cx('chargeback-proof-form__field')}>
          <FormGroup label={intl.messages['app.support.disputes.proof-form.files.upload-label']}>
            <FileUploader
              key="file-uploader"
              input={{
                name: 'files',
                value: '',
                onChange: () => {}
              }}
              meta={{
                touched: false,
                error: null
              }}
              accept={ACCEPT}
              disabled={isUploading}
              onUpload={event => {
                // Save files before event is released
                const files = Array.from(event.target.files);

                return Promise.resolve().then(() => {
                  handleFileUpload(files);
                });
              }}
              content={{
                default: {
                  title: isUploading
                    ? intl.messages['app.support.disputes.proof-form.files.uploading']
                    : intl.messages['app.support.disputes.proof-form.files.upload-placeholder'],
                  description: intl.formatMessage(
                    {id: 'app.support.disputes.proof-form.files.upload-hint'},
                    {maxFiles: MAX_FILES, maxSizeMb: MAX_FILE_SIZE / (1024 * 1024)}
                  ),
                  extra: {
                    children: isUploading
                      ? intl.messages['app.support.disputes.proof-form.files.uploading-short']
                      : intl.messages['app.support.disputes.proof-form.files.browse'],
                    icon: isUploading ? 'loading' : 'upload'
                  }
                }
              }}
            />

            {uploadedFiles.length > 0 && (
              <div style={{marginTop: '12px'}}>
                {uploadedFiles.map(file => (
                  <div
                    key={file.fileId}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '8px 12px',
                      backgroundColor: '#f9fafb',
                      border: '1px solid #e5e7eb',
                      borderRadius: '6px',
                      marginBottom: '8px'
                    }}>
                    <span style={{fontSize: '14px', color: '#374151'}}>{file.fileName}</span>
                    <Button
                      appearance="flatten"
                      size="xs"
                      icon="close"
                      onClick={() => removeFile(file.fileId)}
                      style={{padding: '4px'}}
                    />
                  </div>
                ))}
              </div>
            )}
          </FormGroup>
        </div>

        <div className={cx('chargeback-proof-form__disclaimer')}>
          {intl.messages['app.support.disputes.proof-form.disclaimer']}
        </div>

        <div style={{flex: 1}}></div>

        <div
          className={cx('chargeback-proof-form__actions')}
          style={{
            display: 'flex',
            gap: '12px',
            padding: '20px 0 0 0',
            borderTop: '1px solid #e5e5e5',
            marginTop: '20px'
          }}>
          <Button
            appearance="secondary"
            size="md"
            onClick={onClose}
            disabled={isSubmitting}
            style={{flex: 1}}>
            {intl.messages['app.support.disputes.proof-form.cancel.button']}
          </Button>
          <Button
            type="submit"
            appearance="primary"
            size="md"
            disabled={isSubmitting || isUploading || uploadedFiles.length === 0}
            style={{flex: 1}}>
            {intl.messages['app.support.disputes.table.send-proof']}
          </Button>
        </div>
      </form>
      <EvidenceProofConfirmModal
        isOpen={showConfirmModal}
        onCancel={handleCancelConfirm}
        onConfirm={handleConfirmSend}
      />
    </>
  );
};

ChargebackProofForm.propTypes = {
  intl: PropTypes.object.isRequired,
  disputeId: PropTypes.number.isRequired,
  transactionId: PropTypes.number.isRequired,
  merchantId: PropTypes.number.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool,
  handleSubmit: PropTypes.func.isRequired,
  setNotification: PropTypes.func.isRequired
};

const enhance = compose(
  reduxForm({
    form: 'chargeback-proof-form'
  }),
  injectIntl
);

export default enhance(ChargebackProofForm);
